import { useEffect, useState } from "react"

import { MbaConfiguration, MediaTypeMbaConfig } from "models/mba"
import { Tab, Tabs, styledTabClasses } from "components/Tabs"
import { TableColumnPicker } from "../components/TableColumnPicker"

import { css } from "@emotion/css"
import { set } from "lodash/fp"
import { useI18n } from "providers/I18n"
import { TableColumnHeader } from "../components/TableColumnHeader"

type Props = {
  data?: Partial<MediaTypeMbaConfig>[]
  update: (
    variables: Partial<MbaConfiguration & { clientConfig?: string }>,
  ) => Promise<Partial<MbaConfiguration>> | void
  id?: string
  disabled?: boolean
  configScope?: "CAMPAIGN" | "CLIENT_AGENCY"
}

type Selectable = {
  label: string
  checked: boolean
  id: string
  name: string
  disabled?: boolean
  aggregatable?: boolean
}

export function AggregatedFields(props: Props) {
  const { data, id, update, disabled, configScope } = props
  const { t } = useI18n()
  const tabs = data?.filter((mtc) => !!mtc.semanticCode)
  const defaultTab = tabs?.[0].name

  const [tab, setTab] = useState<string | undefined>(defaultTab)
  const [selectable, setSelectable] = useState<Selectable[]>()
  const [checked, setChecked] = useState<boolean>()

  const viewable = data?.find((mtc) => mtc.name === tab)
  const pickable = viewable?.mediaFields
    ?.filter((mf) => mf.type === "NUMERIC")
    ?.map((mf) => ({
      ...mf,
      label: mf.name,
      checked: mf.aggregatable ?? false,
      disabled,
    }))

  const handleTabChange = (selected: string) => setTab(selected)

  useEffect(() => {
    setTab(defaultTab)
  }, [defaultTab])

  const onCheckBoxChange = () => {
    setChecked(!checked)
    if (data) {
      const updatedSelectable = selectable?.map((s: Selectable) => {
        return {
          ...s,
          checked: !checked,
          aggregatable: !checked,
          disabled,
        }
      })
      setSelectable(updatedSelectable)

      // prepare the updated data and patch
      const dataIndex = data?.findIndex((mtc) => mtc.id === viewable?.id)
      const updated = updatedSelectable?.map((us) => {
        const { label, checked, disabled, ...keep } = us
        return keep
      })
      const _updated = viewable?.mediaFields?.map(
        (mf) => updated?.find((u) => u?.id === mf?.id) || mf,
      )
      const updatedData = set(`[${dataIndex}].mediaFields`, _updated, data)
      !!id && update({ id, configScope, mediaTypes: updatedData })
    }
  }

  const handleVisibleToggle = (fieldId: string) => {
    if (!data) return
    // get the new value
    const updatedIndex = selectable?.findIndex((mf) => mf.id === fieldId) ?? -1
    const aggregatable = !selectable?.[updatedIndex]?.aggregatable

    if (selectable && updatedIndex > -1) {
      // update the local state
      const touched = selectable[updatedIndex]
      const updatedSelectable = set(
        `[${updatedIndex}]`,
        { ...touched, checked: aggregatable, aggregatable },
        selectable,
      )
      setSelectable(updatedSelectable)

      // update total checkbox
      setChecked(
        updatedSelectable?.filter((s) => s?.aggregatable)?.length ===
          pickable?.length,
      )

      // prepare the updated data and patch
      const dataIndex = data?.findIndex((mtc) => mtc.id === viewable?.id)
      const updated = updatedSelectable?.map((us) => {
        const { label, checked, disabled, ...keep } = us
        return keep
      })
      const _updated = viewable?.mediaFields?.map(
        (mf) => updated?.find((u) => u?.id === mf?.id) || mf,
      )
      const updatedData = set(
        `[${dataIndex}].mediaFields`,
        _updated,
        data ?? [],
      )
      !!id && update({ id, configScope, mediaTypes: updatedData })
    }
  }

  useEffect(() => {
    setSelectable(pickable)
    setChecked(
      pickable?.filter((s) => s?.aggregatable)?.length === pickable?.length,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, data])

  return (
    <div>
      <h5
        className="BodyBody-1BlackBold"
        style={{ marginBottom: "var(--spacing-xl)" }}
      >
        {t("mba.media-fields-summarizations.title")}
      </h5>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={tab}
        onTabChange={handleTabChange}
        TabIndicatorProps={{
          style: {
            display: "none",
          },
        }}
        classes={{ flexContainer: styles.tabs }}
      >
        {tabs?.map((tb) => (
          <Tab
            key={tb.semanticCode}
            classes={styledTabClasses}
            className={styles.tab}
            value={tb.name}
            label={tb.name}
            selected={tab === tb.name}
          />
        ))}
      </Tabs>
      <div className={styles.content}>
        {selectable ? (
          <div>
            <div className={styles.title}> {t("mba.fields")}</div>
            <div className={styles.container}>
              <TableColumnHeader
                tab={tab}
                checked={checked}
                onCheckBoxChange={onCheckBoxChange}
                disabled={disabled}
                section="aggregatable"
              />
              <TableColumnPicker
                onChange={handleVisibleToggle}
                columns={selectable}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const styles = {
  tabs: css`
    border-bottom: 1px solid var(--light-grey);
    height: 32px;
    min-width: fit-content;
  `,
  tab: css`
    min-height: var(--spacing-xxl-2);
    &.MuiButtonBase-root {
      background-color: var(--lighter-grey);
      padding: var(--spacing-xs) var(--spacing-m);
      min-width: 0;
      border: solid 1px var(--light-grey);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      border-bottom: solid 1px var(--light-grey);
      span {
        font-size: 13px;
        color: var(--dark-grey);
        text-transform: none;
      }
    }
    &.MuiButtonBase-root.Mui-selected {
      background-color: var(--white);
      border-bottom: solid 1px var(--white);
      span {
        color: var(--black);
        font-weight: normal;
      }
    }
  `,
  content: css`
    border-style: solid;
    border-color: var(--light-grey);
    border-width: 0 1px 1px 1px;
    min-height: 100px;
    margin-top: -16px;
    display: grid;
    grid-template-columns: repeat(2, minmax(380px, max-content));
    grid-column-gap: 1rem;
    align-items: start;
    justify-content: space-between;
    padding: var(--spacing-m) 0;
    padding-left: var(--spacing-xl);
    max-height: 500px;
  `,
  title: css`
    font-weight: 300;
    color: var(--black);
    margin-bottom: 14px;
    font-size: 14px;
  `,
  column: css`
    overflow-y: scroll;
    height: 400px;
  `,
  container: css`
    display: flex;
    flex-direction: column;
  `,
}
